import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { FEATURE_TOGGLE_CONFIG, FeatureToggle } from "config/featureToggles";
import { loadBoolFromStorage } from "utils/storage";

type FeatureTogglesState = Record<FeatureToggle, boolean>;

function buildInitialState(): FeatureTogglesState {
    return Object.fromEntries(
        Object.values(FeatureToggle).map((toggle) => {
            const { defaultValue, storageKey } = FEATURE_TOGGLE_CONFIG[toggle];
            const value = storageKey !== undefined ? loadBoolFromStorage(storageKey, defaultValue) : defaultValue;
            return [toggle, value];
        }),
    ) as FeatureTogglesState;
}

export const featureTogglesSlice = createSlice({
    name: "featureToggles",
    initialState: buildInitialState,
    reducers: {
        setFeatureToggle: (state, action: PayloadAction<{ toggle: FeatureToggle; value: boolean }>) => {
            state[action.payload.toggle] = action.payload.value;
        },
        /**
         * Set one or more toggles to the same boolean value.
         *
         * Usage: `featureTogglesActions.setFeatureToggles(false, FeatureToggle.MiniMapVisible, FeatureToggle.AxisSphereVisible)`
         */
        setFeatureToggles: {
            prepare(value: boolean, ...toggles: FeatureToggle[]) {
                return {
                    payload: Object.fromEntries(toggles.map((t) => [t, value])) as Partial<FeatureTogglesState>,
                };
            },
            reducer(state, action: PayloadAction<Partial<FeatureTogglesState>>) {
                Object.assign(state, action.payload);
            },
        },
    },
});

const { actions, reducer } = featureTogglesSlice;
export { actions as featureTogglesActions, reducer as featureTogglesReducer };
